import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, Trash2, Shield, Search, Sparkles, ShieldOff, X, ShieldHalf, RotateCcw, Eye, Ban, ShieldAlert, HeartHandshake, UsersRound } from "lucide-react";
import OnlineUsersCard from "../components/OnlineUsersCard";
import UserSnapshotDialog from "../components/UserSnapshotDialog";
import UserNameLink from "../components/UserNameLink";
import BanConfirmDialog from "../components/BanConfirmDialog";
import StaffChatCard from "../components/StaffChatCard";
import ModActivityStrip from "../components/ModActivityStrip";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "—";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const NEW_WINDOW_MS = 24 * 3600 * 1000;

export default function AdminUsers() {
  const { user: current } = useAuth();
  const isAdmin = current?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // single-user delete target
  const [snapshotUser, setSnapshotUser] = useState(null); // stealth snapshot target
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [banTarget, setBanTarget] = useState(null); // { user, nextBanned }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [query, setQuery] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Server-side pagination — fetches in 200-row pages so a huge user table
  // never blows up admin memory. `serverSearch` mirrors the query but only
  // updates after a debounce so we don't hammer the endpoint on every keystroke.
  const PAGE_SIZE = 200;
  const [serverSearch, setServerSearch] = useState("");
  const [serverFilter, setServerFilter] = useState(""); // "" | "active_coach"
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounce keystrokes → serverSearch → refetch from backend
  useEffect(() => {
    const t = setTimeout(() => setServerSearch(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: 0 };
      if (serverSearch) params.search = serverSearch;
      if (serverFilter) params.filter = serverFilter;
      const res = await api.get("/admin/users", { params });
      setUsers(res.data || []);
      setHasMore((res.data || []).length >= PAGE_SIZE);
      api.post("/admin/users/mark_seen").catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [serverSearch, serverFilter]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = { limit: PAGE_SIZE, skip: users.length };
      if (serverSearch) params.search = serverSearch;
      if (serverFilter) params.filter = serverFilter;
      const res = await api.get("/admin/users", { params });
      const next = res.data || [];
      // De-dup by id in case the collection changed underneath us.
      setUsers((prev) => {
        const seen = new Set(prev.map((u) => u.id));
        return [...prev, ...next.filter((u) => !seen.has(u.id))];
      });
      setHasMore(next.length >= PAGE_SIZE);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const doDeleteOne = async () => {
    if (!confirmDelete) return;
    try {
      const res = await api.delete(`/admin/users/${confirmDelete.id}`);
      const c = res.data?.cascade || {};
      toast.success(
        `Deleted ${confirmDelete.email}`,
        { description: `${c.shots || 0} shots · ${c.vials || 0} vials · ${c.cycles || 0} cycles · ${c.weight_logs || 0} weight logs removed`, duration: 6000 }
      );
      setConfirmDelete(null);
      setUsers((u) => u.filter((x) => x.id !== confirmDelete.id));
      setSelectedIds((cur) => { const n = new Set(cur); n.delete(confirmDelete.id); return n; });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  // A user is selectable if it isn't the current admin
  const selectableIds = useMemo(
    () => filtered.filter((u) => u.id !== current?.id).map((u) => u.id),
    [filtered, current?.id]
  );
  const selectedInFilter = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)),
    [selectableIds, selectedIds]
  );

  const allSelected = selectableIds.length > 0 && selectedInFilter.length === selectableIds.length;
  const someSelected = selectedInFilter.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds((cur) => {
      const n = new Set(cur);
      if (allSelected) {
        for (const id of selectableIds) n.delete(id);
      } else {
        for (const id of selectableIds) n.add(id);
      }
      return n;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/admin/users/bulk_delete", { user_ids: ids });
      const d = res.data || {};
      const c = d.cascade || {};
      toast.success(
        `Deleted ${d.deleted || 0} user${(d.deleted || 0) === 1 ? "" : "s"}`,
        { description: `${c.shots || 0} shots · ${c.vials || 0} vials · ${c.cycles || 0} cycles · ${c.weight_logs || 0} weight logs cascaded`, duration: 6000 }
      );
      setUsers((u) => u.filter((x) => !ids.includes(x.id)));
      clearSelection();
      setConfirmBulkDelete(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Aggregate cascade preview for bulk delete dialog
  const bulkPreview = useMemo(() => {
    const ids = Array.from(selectedIds);
    const sel = users.filter((u) => ids.includes(u.id));
    return {
      count: sel.length,
      shots: sel.reduce((a, u) => a + (u.shots_count || 0), 0),
      vials: sel.reduce((a, u) => a + (u.vials_count || 0), 0),
      cycles: sel.reduce((a, u) => a + (u.cycles_count || 0), 0),
      hasAdmin: sel.some((u) => u.role === "admin"),
      sample: sel.slice(0, 5).map((u) => u.email),
      more: Math.max(0, sel.length - 5),
    };
  }, [selectedIds, users]);

  const newCount = users.filter((u) => !u.admin_seen).length;
  const totalCount = users.length;
  const admins = users.filter((u) => u.role === "admin").length;
  const now = Date.now();
  const selectionCount = selectedIds.size;

  return (
    <AppLayout>
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#0D9488]" />
            <div className="font-mono-num text-[11px] text-[#0D9488] uppercase tracking-[0.3em]">// Roster</div>
          </div>
          <h1 className="font-display font-black uppercase tracking-tight text-5xl lg:text-6xl leading-none">Users</h1>
          <p className="text-slate-500 mt-3 text-sm">
            {totalCount} account{totalCount === 1 ? "" : "s"} · {admins} admin · {newCount > 0 ? <span className="text-[#0D9488]">{newCount} new signup{newCount === 1 ? "" : "s"}</span> : "no new signups"}
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email or name…"
            className="bg-slate-50 border-slate-200 rounded-none h-11 pl-9"
            data-testid="admin-users-search"
          />
        </div>
      </div>

      {/* Quick filters — server-driven so pagination + counts stay accurate. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-users-filters">
        <span className="text-[10px] uppercase tracking-widest font-mono-num font-semibold text-slate-500">
          Filter:
        </span>
        <button
          type="button"
          onClick={() => setServerFilter("")}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-mono-num uppercase tracking-widest border transition-colors ${
            serverFilter === ""
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}
          data-testid="admin-users-filter-all"
        >
          All users
        </button>
        <button
          type="button"
          onClick={() => setServerFilter(serverFilter === "active_coach" ? "" : "active_coach")}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-mono-num uppercase tracking-widest border transition-colors ${
            serverFilter === "active_coach"
              ? "bg-[#0D9488] text-white border-[#0D9488]"
              : "bg-white text-teal-700 border-teal-200 hover:border-teal-400 hover:bg-teal-50"
          }`}
          data-testid="admin-users-filter-active-coach"
        >
          <UsersRound className="w-3 h-3" />
          Coaches with active groups
        </button>
      </div>

      {/* Today's moderation actions — quick pulse of team activity */}
      <ModActivityStrip />

      {/* Who's online + Staff-only chat side-by-side on wide screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <OnlineUsersCard />
        <StaffChatCard />
      </div>

      {loading ? (
        <div className="border border-slate-200 p-16 text-center font-mono-num text-slate-500 uppercase tracking-widest text-xs" data-testid="admin-users-loading">
          Loading roster…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-slate-200 p-16 text-center">
          <div className="font-display font-black text-3xl uppercase text-slate-500">No users found</div>
          <p className="text-slate-500 mt-2 text-sm">{query ? "Adjust your search." : "Registered users will appear here."}</p>
        </div>
      ) : (
        <div className="border border-slate-200 overflow-hidden bg-white" data-testid="admin-users-table-wrap">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-num">
                <tr>
                  <th className="w-10 px-3 py-3 text-left">
                    {isAdmin ? (
                      <Checkbox
                        checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleAll}
                        disabled={selectableIds.length === 0}
                        className="border-zinc-600 data-[state=checked]:bg-[#0D9488] data-[state=checked]:border-[#0D9488] data-[state=checked]:text-black data-[state=indeterminate]:bg-[#0D9488] data-[state=indeterminate]:border-[#0D9488] data-[state=indeterminate]:text-black"
                        aria-label="Select all users"
                        data-testid="admin-users-select-all"
                      />
                    ) : null}
                  </th>
                  <th className="text-left px-4 py-3 font-normal">Account</th>
                  <th className="text-left px-4 py-3 font-normal">Role</th>
                  <th className="text-left px-4 py-3 font-normal">Joined</th>
                  <th className="text-right px-4 py-3 font-normal">Shots</th>
                  <th className="text-right px-4 py-3 font-normal">Vials</th>
                  <th className="text-right px-4 py-3 font-normal">Cycles</th>
                  <th className="text-left px-4 py-3 font-normal">Last shot</th>
                  <th className="text-center px-4 py-3 font-normal">Premium</th>
                  <th className="text-center px-4 py-3 font-normal">Coach</th>
                  <th className="text-right px-4 py-3 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isNew = !u.admin_seen || (now - new Date(u.created_at).getTime() < NEW_WINDOW_MS);
                  const isSelf = current?.id === u.id;
                  const targetIsAdmin = u.role === "admin";
                  const isMod = u.role === "moderator";
                  // Moderators cannot ban admins or other moderators. Nobody bans an admin from the UI.
                  const canBan = !targetIsAdmin && !(current?.role === "moderator" && isMod);
                  const checked = selectedIds.has(u.id);
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-slate-200 last:border-b-0 transition-colors ${checked ? "bg-[#0D9488]/[0.06]" : (!u.admin_seen ? "bg-[#0D9488]/[0.04]" : "hover:bg-slate-50/40")}`}
                      data-testid={`admin-user-row-${u.id}`}
                    >
                      <td className="px-3 py-3">
                        {(isSelf || !isAdmin) ? (
                          <span className="inline-block w-4 h-4" aria-hidden />
                        ) : (
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleOne(u.id)}
                            className="border-zinc-600 data-[state=checked]:bg-[#0D9488] data-[state=checked]:border-[#0D9488] data-[state=checked]:text-black"
                            aria-label={`Select ${u.email}`}
                            data-testid={`admin-user-checkbox-${u.id}`}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isNew && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/50 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest"
                              data-testid={`admin-user-new-${u.id}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" /> New
                            </span>
                          )}
                          <div>
                            <UserNameLink userId={u.id} handle={u.handle} name={u.name || "—"} className="text-slate-900 font-medium" showUnderline={false} testid={`admin-user-name-link-${u.id}`} />
                            <div className="text-slate-500 text-xs font-mono-num break-all">{u.email}</div>
                            {u.banned && (
                              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-500/40 bg-red-500/5 text-red-400 font-mono-num text-[9px] uppercase tracking-widest" data-testid={`admin-user-banned-${u.id}`}>
                                <Ban className="w-2.5 h-2.5" /> Banned{u.banned_reason ? ` — ${u.banned_reason}` : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isSelf ? (
                          targetIsAdmin ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#0D9488]/40 text-[#0D9488] font-mono-num text-[10px] uppercase tracking-widest">
                              <Shield className="w-3 h-3" /> Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 border border-slate-200 text-slate-600 font-mono-num text-[10px] uppercase tracking-widest">
                              {u.role || "user"}
                            </span>
                          )
                        ) : isAdmin ? (
                          <select
                            value={u.role || "user"}
                            onChange={async (e) => {
                              const newRole = e.target.value;
                              const prev = u.role || "user";
                              setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
                              try {
                                await api.patch(`/admin/users/${u.id}/role`, { role: newRole });
                                toast.success(`${u.email} is now ${newRole}`);
                              } catch (err) {
                                toast.error(formatApiError(err));
                                setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, role: prev } : x));
                              }
                            }}
                            className="bg-slate-50 border border-slate-200 rounded-none h-8 px-2 text-[10px] font-mono-num uppercase tracking-widest text-slate-900 hover:border-[#0D9488]/50 focus:outline-none focus:border-[#0D9488]"
                            data-testid={`admin-user-role-${u.id}`}
                          >
                            <option value="user">User</option>
                            <option value="moderator">Moderator</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 border border-slate-200 text-slate-600 font-mono-num text-[10px] uppercase tracking-widest" data-testid={`admin-user-role-readonly-${u.id}`}>
                            {u.role === "admin" && <Shield className="w-3 h-3 text-red-400" />}
                            {u.role === "moderator" && <ShieldHalf className="w-3 h-3 text-[#0D9488]" />}
                            {u.role || "user"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-800 font-mono-num">{timeAgo(u.created_at)}</td>
                      <td className="px-4 py-3 text-right text-slate-900 font-mono-num tabular-nums">{u.shots_count}</td>
                      <td className="px-4 py-3 text-right text-slate-900 font-mono-num tabular-nums">{u.vials_count}</td>
                      <td className="px-4 py-3 text-right text-slate-900 font-mono-num tabular-nums">{u.cycles_count}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono-num text-xs">{timeAgo(u.last_shot_at)}</td>
                      <td className="px-4 py-3 text-center">
                        {isSelf ? (
                          <span className="text-slate-400 font-mono-num text-[10px]">—</span>
                        ) : !isAdmin ? (
                          <span className="text-slate-400 font-mono-num text-[10px]" title="Admin-only" data-testid={`admin-user-premium-readonly-${u.id}`}>
                            {u.has_premium ? "★" : "—"}
                          </span>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={async () => {
                                const next = !u.has_premium;
                                setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, has_premium: next } : x));
                                try {
                                  await api.patch(`/admin/users/${u.id}/premium`, { has_premium: next });
                                  toast.success(next ? `${u.email} granted premium` : `${u.email} premium revoked`);
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                  setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, has_premium: !next } : x));
                                }
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-1 border font-mono-num text-[10px] uppercase tracking-widest transition-colors ${
                                u.has_premium
                                  ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/20"
                                  : "border-slate-200 text-slate-500 hover:border-[#0D9488] hover:text-[#0D9488]"
                              }`}
                              data-testid={`admin-user-premium-${u.id}`}
                              title={u.has_premium ? "Click to revoke premium" : "Click to grant premium (Playbook members-only access)"}
                            >
                              ★ {u.has_premium ? "Member" : "Grant"}
                            </button>
                            {u.has_premium && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const { data } = await api.get(`/admin/users/${u.id}/last-payment`);
                                    if (!data.has_refundable) {
                                      toast.error("No refundable PayPal capture on file for this user (manually granted?).");
                                      return;
                                    }
                                    if (!window.confirm(`Refund $${Number(data.amount_usd).toFixed(2)} to ${u.email}? Their premium will be revoked immediately.`)) return;
                                    await api.post(`/admin/billing/refund/${data.payment_id}`);
                                    toast.success(`Refunded $${Number(data.amount_usd).toFixed(2)} to ${u.email}`);
                                    setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, has_premium: false } : x));
                                  } catch (err) {
                                    toast.error(formatApiError(err));
                                  }
                                }}
                                className="inline-flex items-center justify-center w-6 h-6 border border-slate-200 hover:border-red-500/70 hover:text-red-400 text-slate-500 transition-colors"
                                data-testid={`admin-user-refund-${u.id}`}
                                title="Refund last PayPal capture + revoke premium"
                              >
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isSelf ? (
                          <span className="text-slate-400 font-mono-num text-[10px]">—</span>
                        ) : !isAdmin ? (
                          <span className="text-slate-400 font-mono-num text-[10px]" title="Admin-only" data-testid={`admin-user-coach-readonly-${u.id}`}>
                            {u.is_coach ? "✓" : "—"}
                          </span>
                        ) : (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={async () => {
                                const next = !u.is_coach;
                                setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, is_coach: next } : x));
                                try {
                                  await api.patch(`/admin/users/${u.id}/coach`, { is_coach: next });
                                  toast.success(next ? `${u.email} verified as coach` : `${u.email} coach status revoked`);
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                  setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, is_coach: !next } : x));
                                }
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-1 border font-mono-num text-[10px] uppercase tracking-widest transition-colors ${
                                u.is_coach
                                  ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/20"
                                  : "border-slate-200 text-slate-500 hover:border-[#0D9488] hover:text-[#0D9488]"
                              }`}
                              data-testid={`admin-user-coach-${u.id}`}
                              title={u.is_coach ? "Click to revoke coach status" : "Click to verify this user as a coach (unlocks My Clients tab)"}
                            >
                              <HeartHandshake className="w-3 h-3" /> {u.is_coach ? "Coach" : "Verify"}
                            </button>
                            {u.active_group_id && (
                              <a
                                href={`/groups/${u.active_group_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 border border-amber-300 bg-amber-50 text-amber-800 font-mono-num text-[10px] uppercase tracking-widest hover:bg-amber-100"
                                title={`Running: ${u.active_group_name}`}
                                data-testid={`admin-user-active-group-${u.id}`}
                              >
                                <UsersRound className="w-3 h-3" />
                                <span className="max-w-[100px] truncate normal-case">{u.active_group_name}</span>
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isSelf ? (
                          <span className="inline-flex items-center gap-1 text-slate-500 font-mono-num text-[10px] uppercase tracking-widest" data-testid={`admin-user-self-${u.id}`}>
                            <ShieldOff className="w-3 h-3" /> You
                          </span>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSnapshotUser(u)}
                                className="h-8 rounded-none text-slate-600 hover:text-[#0D9488] hover:bg-[#0D9488]/10 border border-transparent hover:border-[#0D9488]/30"
                                data-testid={`admin-user-view-${u.id}`}
                                title="View user data"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!canBan}
                              onClick={() => setBanTarget({ user: u, nextBanned: !u.banned })}
                              className={`h-8 rounded-none border border-transparent disabled:opacity-30 disabled:cursor-not-allowed ${
                                u.banned
                                  ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/30"
                                  : "text-slate-600 hover:text-orange-400 hover:bg-orange-500/10 hover:border-orange-500/30"
                              }`}
                              data-testid={`admin-user-ban-${u.id}`}
                              title={
                                !canBan
                                  ? (targetIsAdmin ? "Cannot ban an admin — demote them first" : "Moderators cannot ban other moderators")
                                  : (u.banned ? "Unban this user" : "Ban this user")
                              }
                            >
                              {u.banned ? <ShieldAlert className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDelete(u)}
                                className="h-8 rounded-none text-slate-600 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 border border-transparent hover:border-[#FF3B30]/30"
                                data-testid={`admin-user-delete-${u.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Server-side pagination — "Load more" pages the next 200 users
              on demand so the admin table stays snappy at scale. Hidden
              once the last page has been fetched. */}
          {(hasMore || loadingMore) && (
            <div className="mt-4 flex flex-col items-center gap-2" data-testid="admin-users-load-more-row">
              <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                Showing {users.length} user{users.length === 1 ? "" : "s"}{serverSearch ? ` matching “${serverSearch}”` : ""}
              </div>
              <Button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="h-9 bg-white hover:bg-slate-50 text-slate-800 rounded-none border border-slate-200 uppercase tracking-widest text-[10px] font-mono-num"
                data-testid="admin-users-load-more"
              >
                {loadingMore ? "Loading…" : `Load next ${PAGE_SIZE}`}
              </Button>
            </div>
          )}
          {!hasMore && users.length > 0 && (
            <div className="mt-3 text-center font-mono-num text-[10px] uppercase tracking-widest text-slate-400" data-testid="admin-users-page-footer">
              End of list · {users.length} user{users.length === 1 ? "" : "s"} shown
            </div>
          )}
        </div>
      )}

      {/* Sticky bulk action bar — admin only. Moderators can't bulk-delete accounts. */}
      {isAdmin && selectionCount > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 border border-[#0D9488]/50 bg-white/95 backdrop-blur px-5 py-3 shadow-xl"
          data-testid="admin-users-bulk-bar"
        >
          <div className="flex items-center gap-2">
            <span className="font-display font-black text-2xl text-[#0D9488] tabular-nums">{selectionCount}</span>
            <span className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-600">
              selected
            </span>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-slate-500 hover:text-slate-900 font-mono-num text-[11px] uppercase tracking-widest flex items-center gap-1"
            data-testid="admin-users-bulk-clear"
          >
            <X className="w-3 h-3" /> Clear
          </button>
          <div className="w-px h-8 bg-zinc-800" />
          <Button
            onClick={() => setConfirmBulkDelete(true)}
            className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold text-white h-10 px-4"
            data-testid="admin-users-bulk-delete"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete {selectionCount} user{selectionCount === 1 ? "" : "s"}
          </Button>
        </div>
      )}

      {/* Ban / unban confirm — branded */}
      <BanConfirmDialog
        user={banTarget?.user}
        nextBanned={banTarget?.nextBanned}
        open={!!banTarget}
        onOpenChange={(o) => { if (!o) setBanTarget(null); }}
        onConfirm={async (reason) => {
          const u = banTarget?.user;
          const next = !!banTarget?.nextBanned;
          if (!u) return;
          const prev = { banned: !!u.banned, reason: u.banned_reason || null };
          setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, banned: next, banned_reason: next ? reason : null } : x));
          try {
            await api.patch(`/admin/users/${u.id}/ban`, { banned: next, reason });
            toast.success(next ? `Banned ${u.name || u.email}` : `Unbanned ${u.name || u.email}`);
            setBanTarget(null);
          } catch (err) {
            setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, banned: prev.banned, banned_reason: prev.reason } : x));
            toast.error(err?.response?.data?.detail || "Ban update failed");
          }
        }}
      />

      {/* Single-user confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900" data-testid="admin-user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete {confirmDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 space-y-1">
              <span className="block">This permanently removes their account and cascades:</span>
              <span className="block font-mono-num text-xs text-slate-500">
                • {confirmDelete?.shots_count || 0} shots · {confirmDelete?.vials_count || 0} vials · {confirmDelete?.cycles_count || 0} cycles · all weight logs
              </span>
              <span className="block text-[#FF3B30]">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="admin-user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteOne}
              className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold"
              data-testid="admin-user-delete-confirm"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk confirm */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900 max-w-md" data-testid="admin-users-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">
              Delete {bulkPreview.count} user{bulkPreview.count === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 space-y-2">
              <span className="block font-mono-num text-xs text-slate-500">
                Cascading: {bulkPreview.shots} shots · {bulkPreview.vials} vials · {bulkPreview.cycles} cycles · all weight logs
              </span>
              {bulkPreview.hasAdmin && (
                <span className="block text-[#0D9488] font-mono-num text-xs uppercase tracking-widest">
                  ⚠ selection includes admin account(s)
                </span>
              )}
              <div className="border border-slate-200 bg-slate-50 p-3 max-h-32 overflow-y-auto space-y-0.5" data-testid="admin-users-bulk-list">
                {bulkPreview.sample.map((email) => (
                  <div key={email} className="font-mono-num text-[11px] text-slate-600 break-all">• {email}</div>
                ))}
                {bulkPreview.more > 0 && (
                  <div className="font-mono-num text-[11px] text-slate-500 pt-1">…and {bulkPreview.more} more</div>
                )}
              </div>
              <span className="block text-[#FF3B30]">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="admin-users-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold disabled:opacity-60"
              data-testid="admin-users-bulk-confirm"
            >
              {bulkDeleting ? "Deleting…" : `Delete ${bulkPreview.count}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stealth user snapshot */}
      <UserSnapshotDialog
        open={!!snapshotUser}
        userId={snapshotUser?.id}
        email={snapshotUser?.email}
        onClose={() => setSnapshotUser(null)}
      />
    </AppLayout>
  );
}
